import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { addDays, format, isSameDay, isToday } from 'date-fns';
import NowIndicator from './NowIndicator';
import WeekDayColumn from './WeekDayColumn';
import LessonCard from './LessonCard';
import useLessonDrag from '../../hooks/useLessonDrag';
import { useLocale } from '../../context/LocaleContext';
import { dayLayout } from '../../domain/calendarGrid';
import { HOURS, HOUR_HEIGHT, GRID_HEIGHT, GUTTER_PX, asDate, openScrollTop } from './weekGrid';
import s from './WeekView.module.css';

// The week grid. Assembly: the geometry is in weekGrid, the pointer handling in
// useLessonDrag, and a day's contents in WeekDayColumn.
//
// ── The all-day row, above the grid (#160) ─────────────────────────────────
//
// MMS's Week and Day draw an all-day event as a band above the time grid,
// labelled `all-day` in the gutter, the event a chip spanning its day's
// column. The band sits in the header block, between the day headings and the
// scrolling body, so it stays put while the grid scrolls: an all-day event has
// no place on a clock to scroll to.
//
// It is rendered only when the week has one, which is ours: an empty band
// would be a permanent strip above the grid, the screen's scarcest space.
// Two all-day events on one date stack; whether MMS stacks them or collapses
// them behind a "+1" is unchecked (#160).
//
// ── One grid, seven columns or one (#572) ───────────────────────────────────
//
// The Day view is this grid one column wide, and Week · Agenda is this
// component (the toolbar's Week submenu, `WEEK_MODES` in
// hooks/useCalendarView.js). So `days` may be handed in; with none it is the
// seven from `weekStart`. Every rule below — the all-day band, the drags, the
// now line — is the same for one column.
//
// ── One block per occurrence, side by side, over the whole day (#749) ───────
//
// `blocks` is one row per occurrence (`useCalendarOccurrences`' `blocks`): a
// group lesson is one block, titled `2 students`. Each column's timed blocks
// are laid out by the core's lane rule (`dayLayout`, `day_layout` in
// core/src/week.rs), shared with the phones' Week grid, and a block is drawn
// `1 / lanes` of the column wide at lane `lane`.
//
// As in MMS, Week and Day alike: overlapping events get equal-width
// side-by-side columns. Two at the same time are half width each; a partial
// overlap puts the later one on the right; a chain of three (4:00, 4:30, 5:00)
// uses two columns, the 5:00 back in the left one; nothing widens while an
// overlapping neighbour exists. A half-width block still shows its time, its
// title and its category, one line each (LessonCard's `narrow`). The grid is
// the whole 24 hours in a scroller, opened with 7:00 AM at the top.
export default function WeekView({
  weekStart, days: daysIn, blocks, categoryColorFor, onLessonClick, onSlotClick,
  onResizeLesson, onMoveLesson,
}) {
  // The Region's day names and clock (#534): `Mo 28` and `07:00` in Germany,
  // `Mon 28` and `7 AM` in the United States, as MMS writes them.
  const { formatWeekdayShort, formatHour } = useLocale();
  const gridRef = useRef(null);
  const dayColRefs = useRef([]);
  const days = daysIn || Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const {
    startCreate, startResize, startMove, ghostFor, liveDurationFor, isMoving, clickSuppressed,
  } = useLessonDrag({
    days, dayColRefs, onSlotClick, onResizeLesson, onMoveLesson, isSameDay,
  });
  // A drag's own release is not a click on the lesson (useLessonDrag.js).
  const clickLesson = (lesson, el) => {
    if (clickSuppressed()) return;
    onLessonClick?.(lesson, el);
  };

  // Open with 7:00 at the top (#749), as MMS does with its default "Initial
  // Display Time", on every new week or day. A layout effect, so the first
  // paint is already scrolled rather than at midnight for a frame.
  useLayoutEffect(() => {
    if (!gridRef.current) return;
    gridRef.current.scrollTop = openScrollTop();
  }, [days[0]?.getTime()]);

  // Split before anything positions a lesson: an all-day event has no start
  // minute, and `getTopOffset` would read the wall-clock time the flag says
  // not to read.
  const timed = blocks.filter((l) => !l.allDay);
  const allDay = blocks.filter((l) => l.allDay);
  const dayKeys = days.map((d) => d.getTime()).join();
  // Each column's blocks with where the core puts them: `{ lesson, place }`.
  // Laid out from the stored length, so a block being resized grows down
  // without its neighbours changing width under the pointer.
  const placedByDay = useMemo(() => days.map((day) => {
    const here = timed.filter((l) => isSameDay(asDate(l.date), day));
    const places = dayLayout(here.map((l) => ({
      id: l.id, startTime: l.startTime ?? format(asDate(l.date), 'HH:mm'), durationMin: l.durationMin,
    })));
    return here.map((lesson, i) => ({ lesson, place: places[i] }));
  }), [blocks, dayKeys]);
  const allDayByDay = days.map((day) => allDay.filter((l) => isSameDay(asDate(l.date), day)));
  const hasAllDay = allDay.length > 0;

  return (
    <div
      className={s.weekView}
      style={{ '--hour-height': `${HOUR_HEIGHT}px`, '--day-count': days.length }}
    >
      <div className={s.weekHeader}>
        <div className={s.weekHeaderGutter} />
        {days.map((day) => (
          <div key={day.toISOString()} className={s.weekHeaderDay}>
            <div className={s.weekHeaderDayName}>{formatWeekdayShort(format(day, 'yyyy-MM-dd'))}</div>
            <div className={`${s.weekHeaderDayNum}${isToday(day) ? ` ${s.today}` : ''}`}>
              {format(day, 'd')}
            </div>
          </div>
        ))}
      </div>

      {hasAllDay ? (
        <div className={s.weekAllDay}>
          <div className={s.weekAllDayGutter}>all-day</div>
          {days.map((day, dayIndex) => (
            <div key={day.toISOString()} className={s.weekAllDayCol}>
              {allDayByDay[dayIndex].map((lesson) => (
                <LessonCard
                  key={lesson.id}
                  lesson={lesson}
                  categoryColorFor={categoryColorFor}
                  onClick={(e) => clickLesson(lesson, e.currentTarget)}
                />
              ))}
            </div>
          ))}
        </div>
      ) : null}

      <div className={s.weekBody} ref={gridRef}>
        <div className={s.weekTimeCol}>
          {HOURS.map((hour) => (
            <div key={hour} className={s.weekTimeSlot}>
              <span className={s.weekTimeLabel}>{formatHour(hour)}</span>
            </div>
          ))}
        </div>

        {days.map((day, dayIndex) => (
          <WeekDayColumn
            key={day.toISOString()}
            day={day}
            dayIndex={dayIndex}
            placed={placedByDay[dayIndex]}
            categoryColorFor={categoryColorFor}
            colRef={(el) => { dayColRefs.current[dayIndex] = el; }}
            ghost={ghostFor(dayIndex)}
            onPointerDown={startCreate}
            onLessonClick={clickLesson}
            onResizeStart={onResizeLesson ? startResize : undefined}
            onMoveStart={onMoveLesson ? startMove : undefined}
            liveDurationFor={liveDurationFor}
            isMoving={isMoving}
          />
        ))}

        <div className={s.nowLayer} style={{ left: GUTTER_PX, height: GRID_HEIGHT }}>
          <NowIndicator weekStart={days[0]} dayCount={days.length} />
        </div>
      </div>
    </div>
  );
}
